import { useIntl } from "react-intl";

export const locales = {
  "en-US": { vstFormat: "en", label: "English", dir: "ltr" },
  "ar-SA": { vstFormat: "ar", label: "العربية", dir: "rtl" },
  "bg-BG": { vstFormat: "bg", label: "Български", dir: "ltr" },
  "ca-ES": { vstFormat: "ca", label: "Català", dir: "ltr" },
  "cs-CZ": { vstFormat: "cs", label: "čeština", dir: "ltr" },
  "cy-GB": { vstFormat: "cy", label: "Cymraeg", dir: "ltr" },
  "da-DK": { vstFormat: "da", label: "Dansk", dir: "ltr" },
  "de-DE": { vstFormat: "de", label: "Deutsch", dir: "ltr" },
  "el-GR": { vstFormat: "el", label: "ελληνικά", dir: "ltr" },
  "en-GB": {
    vstFormat: "en_GB",
    label: "English (UK)",
    dir: "ltr",
  },
  "es-ES": { vstFormat: "es", label: "Español", dir: "ltr" },
  "es-MX": {
    vstFormat: "es_MX",
    label: "Español - México",
    dir: "ltr",
  },
  "fi-FI": { vstFormat: "fi", label: "Suomi", dir: "ltr" },
  "tl-PH": { vstFormat: "fil", label: "Tagalog", dir: "ltr" },
  "fr-CA": {
    vstFormat: "fr_CA",
    label: "Français - Canada",
    dir: "ltr",
  },
  "fr-FR": { vstFormat: "fr", label: "Français", dir: "ltr" },
  "ga-IE": { vstFormat: "ga", label: "Gaeilge", dir: "ltr" },
  "he-IL": { vstFormat: "he", label: "עברית", dir: "rtl" },
  "hi-IN": { vstFormat: "hi", label: "हिंदी", dir: "ltr" },
  "hu-HU": { vstFormat: "hu", label: "Magyar", dir: "ltr" },
  "is-IS": { vstFormat: "is", label: "Íslenska", dir: "ltr" },
  "it-IT": { vstFormat: "it", label: "Italiano", dir: "ltr" },
  "ja-JP": { vstFormat: "ja", label: "日本語", dir: "ltr" },
  "ko-KR": { vstFormat: "ko", label: "한국어", dir: "ltr" },
  "nl-NL": { vstFormat: "nl", label: "Nederlands", dir: "ltr" },
  "nb-NO": { vstFormat: "no", label: "Norsk", dir: "ltr" },
  "pl-PL": { vstFormat: "pl", label: "Polskie", dir: "ltr" },
  "pt-BR": {
    vstFormat: "pt_BR",
    label: "Português do Brasil",
    dir: "ltr",
  },
  "pt-PT": { vstFormat: "pt", label: "Português", dir: "ltr" },
  "ro-RO": { vstFormat: "ro", label: "Română", dir: "ltr" },
  "ru-RU": { vstFormat: "ru", label: "Pусский", dir: "ltr" },
  "sv-SE": { vstFormat: "sv", label: "Svensk", dir: "ltr" },
  "th-TH": { vstFormat: "th", label: "ภาษาไทย", dir: "ltr" },
  "tr-TR": { vstFormat: "tr", label: "Türkçe", dir: "ltr" },
  "vi-VN": { vstFormat: "vi", label: "Tiếng Việt", dir: "ltr" },
  "zh-CN": {
    vstFormat: "zh_CN",
    label: "中文简体",
    dir: "ltr",
  },
  "zh-TW": {
    vstFormat: "zh_TW",
    label: "繁體中文",
    dir: "ltr",
  },
} as const;

export type FullLocale = keyof typeof locales;
export type VstLocale = (typeof locales)[FullLocale]["vstFormat"];
export type LocaleDir = "rtl" | "ltr";

export const getFullLocaleFromVstLocale = (
  vstLocale: VstLocale
): FullLocale => {
  return Object.keys(locales).find(
    (locale) => locales[locale as FullLocale]?.vstFormat === vstLocale
  ) as FullLocale;
};

export const getVstLocaleFromFullLocale = (
  fullLocale: FullLocale
): VstLocale => {
  return locales[fullLocale].vstFormat;
};

export const getDirFromFullLocale = (fullLocale: FullLocale): LocaleDir => {
  return locales[fullLocale].dir;
};

export function isVstLocale(locale?: string): locale is VstLocale {
  return !!locale && Object.values(locales).some((l) => l.vstFormat === locale);
}

export function isFullLocale(locale?: string): locale is FullLocale {
  return !!locale && Object.keys(locales).includes(locale);
}

export function useCurrentFullLocale(): FullLocale {
  // It should not be possible for the intl locale to be an invalid value
  return useIntl().locale as FullLocale;
}

export function useCurrentVstLocale(): VstLocale {
  return getVstLocaleFromFullLocale(useCurrentFullLocale());
}

export function useCurrentLocaleDirection(): LocaleDir {
  return getDirFromFullLocale(useCurrentFullLocale());
}
